import { Fragment } from "react";
import { useRouteLoaderData, useSubmit } from "react-router-dom";
 import services from "./services";
import processes from "./processes";
import { Link } from "react-router-dom";
import { testerEmails } from "../common/functions";
import UserInfo from "../common/UserInfo";
import { WorkOrder, assignmentText } from "./types";
import { useAuthStore, User } from "../main/authStore";
import WorkOrderFileSystem from "./WorkOrderFileSystem";
type FieldDef = {
    label: string;
    value?: React.ReactNode;
    show?: boolean;
};
const transitionLabels: Partial<Record<WorkOrder["workOrderStatus"], string[]>> = {
    Queue: ["Active"],
    Active: ["Parked", "Reporting", "Hold"],
    Parked: ["Active", "Hold"],
    Reporting: ["Report Assist", "Ready for Pickup", "Hold"],
    "Report Assist": ["Ready for Pickup", "Hold"],
    "Ready for Pickup": ["Completed", "Hold"],
};
const getTransitionButtonLabel = (
    currentStatus: WorkOrder["workOrderStatus"],
    nextStatus: WorkOrder["workOrderStatus"]
) => {
    if(currentStatus === "Queue" && nextStatus === "Active") return "Start";
    if(currentStatus === "Active" && nextStatus === "Parked") return "Park";
    if(currentStatus === "Parked" && nextStatus === "Active") return "Continue";
    if(currentStatus === "Ready for Pickup" && nextStatus === "Completed") return "Complete";
    return nextStatus;
};
export default function WorkOrderDetail(){
    const user = useAuthStore(state => state.user) as User;
    const workOrder = useRouteLoaderData("workOrder") as WorkOrder;
    const submit = useSubmit();
    const handleDelete = async (id: number) => {
        if(!window.confirm("Are you sure to delete this work order?\n\nThis action cannot be undone.")) return;
        const formData = new FormData();
        formData.append("id", String(id));
        submit(formData, {
            method: "post",
            action: "Delete"
        });
    };
    const handleValidate = (id:number) => {
        if(!window.confirm("Are you sure to validate this work order?")) return;
        let validateReason = window.prompt("reason to validdate:");
        if(validateReason === null) return;
        while(validateReason === ""){
            window.alert("Reason is required to validate.");
            validateReason = window.prompt("reason to validate:");
            if(validateReason === null) return;
        }
        const formData = new FormData();
        formData.append("validateReason", validateReason);
        formData.append("id", String(id));
        submit(formData, {
            method: "post",
            action: "Validate"
        });
    }
    const handleVoid = (id: number) => {
        if(!window.confirm("Are you sure to void this work order?")) return;
        let voidReason = window.prompt("reason to void:");
        if(voidReason === null) return;
        while(voidReason === ""){
            window.alert("Reason is required to void.");
            voidReason = window.prompt("reason to void:");
            if(voidReason === null) return;
        }
        const formData = new FormData();
        formData.append("voidReason", voidReason);
        formData.append("id", String(id));
        submit(formData, {
            method: "post",
            action: "Void"
        });
    }
    const handleDownloadInfoPDF = async() => {
        const formData = new FormData();
        formData.append("id", String(workOrder.id));
        submit(formData, {method: "post", action: "GenerateReqPDF"});
    }
    const handleDownloadStatusPdf = async() => {
        const formData = new FormData();
        formData.append("id", String(workOrder.id));
        submit(formData, {method: "post", action: "GenerateStatusPDF"});
    }
    const handleDownloadScanningPdf = async() => {
        const formData = new FormData();
        formData.append("id", String(workOrder.id));
        submit(formData, {method: "post", action: "GenerateScanPDF"});
    }
    const totalRequestedQuantity = (workOrder.infoData ?? []).reduce((total, item) => {
        const qty = String(item.qty ?? "").trim();
        return /^\d+$/.test(qty) ? total + Number(qty) : total;
    }, 0);
    const previousNonHoldStatus = workOrder.workOrderStatus === "Hold"
        ? workOrder.workOrderStatusHistory
            ?.find(entry => entry.status !== "Hold")
            ?.status
        : null;
    const promptReportAssistFields = () => {
        let acceptedQuantity = window.prompt("accepted quantity:");
        if(acceptedQuantity === null) return null;
        while(!/^\d+$/.test(acceptedQuantity.trim())){
            window.alert("Accepted Quantity is required and must be a non-negative integer.");
            acceptedQuantity = window.prompt("accepted quantity:");
            if(acceptedQuantity === null) return null;
        }
        let rejectedQuantity = window.prompt("rejected quantity:");
        if(rejectedQuantity === null) return null;
        while(!/^\d+$/.test(rejectedQuantity.trim())){
            window.alert("Rejected Quantity is required and must be a non-negative integer.");
            rejectedQuantity = window.prompt("rejected quantity:");
            if(rejectedQuantity === null) return null;
        }
        while(Number(acceptedQuantity.trim()) + Number(rejectedQuantity.trim()) > totalRequestedQuantity){
            window.alert(`Accepted Quantity + Rejected Quantity must be less than or equal to total quantity (${totalRequestedQuantity}).`);
            acceptedQuantity = window.prompt("accepted quantity:", acceptedQuantity.trim());
            if(acceptedQuantity === null) return null;
            while(!/^\d+$/.test(acceptedQuantity.trim())){
                window.alert("Accepted Quantity is required and must be a non-negative integer.");
                acceptedQuantity = window.prompt("accepted quantity:", acceptedQuantity?.trim() ?? "");
                if(acceptedQuantity === null) return null;
            }
            rejectedQuantity = window.prompt("rejected quantity:", rejectedQuantity.trim());
            if(rejectedQuantity === null) return null;
            while(!/^\d+$/.test(rejectedQuantity.trim())){
                window.alert("Rejected Quantity is required and must be a non-negative integer.");
                rejectedQuantity = window.prompt("rejected quantity:", rejectedQuantity?.trim() ?? "");
                if(rejectedQuantity === null) return null;
            }
        }
        let reportNotes = window.prompt("report notes:");
        if(reportNotes === null) return null;
        while(reportNotes.trim() === ""){
            window.alert("Report Notes is required.");
            reportNotes = window.prompt("report notes:");
            if(reportNotes === null) return null;
        }
        const inspectorLabels = (workOrder.techs ?? []).map(tech => tech.label);
        const inspectorOptions = inspectorLabels.join(", ");
        let inspectorInitials = window.prompt(`inspector initials (${inspectorOptions || "selected technicians"}):`);
        if(inspectorInitials === null) return null;
        let trimmedInspectorInitials = inspectorInitials.trim();
        while(
            trimmedInspectorInitials === "" ||
            !inspectorLabels.includes(trimmedInspectorInitials)
        ){
            window.alert("Inspector Initials is required and must match one of the selected technicians.");
            inspectorInitials = window.prompt(`inspector initials (${inspectorOptions || "selected technicians"}):`);
            if(inspectorInitials === null) return null;
            trimmedInspectorInitials = inspectorInitials.trim();
        }
        return {
            acceptedQuantity: acceptedQuantity.trim(),
            rejectedQuantity: rejectedQuantity.trim(),
            reportNotes: reportNotes.trim(),
            inspectorInitials: trimmedInspectorInitials
        };
    };
    const handleTransitionStatus = (nextStatus: WorkOrder["workOrderStatus"]) => {
        const formData = new FormData();
        formData.append("id", String(workOrder.id));
        formData.append("workOrderStatus", nextStatus);
        if(nextStatus === "Hold"){
            let holdReason = window.prompt("hold reason:");
            if(holdReason === null) return;
            while(holdReason.trim() === ""){
                window.alert("Hold Reason is required.");
                holdReason = window.prompt("hold reason:");
                if(holdReason === null) return;
            }
            formData.append("holdReason", holdReason.trim());
        }
        if(nextStatus === "Report Assist"){
            const reportAssistData = promptReportAssistFields();
            if(!reportAssistData) return;
            formData.append("acceptedQuantity", reportAssistData.acceptedQuantity);
            formData.append("rejectedQuantity", reportAssistData.rejectedQuantity);
            formData.append("reportNotes", reportAssistData.reportNotes);
            formData.append("inspectorInitials", reportAssistData.inspectorInitials);
        }
        submit(formData, {
            method: "post",
            action: "TransitionStatus"
        });
    };
    const transitionStatuses = workOrder.workOrderStatus === "Hold"
        ? (previousNonHoldStatus ? [previousNonHoldStatus] : [])
        : ((transitionLabels[workOrder.workOrderStatus] ?? []) as WorkOrder["workOrderStatus"][]);
    const statusFieldValue = (
        <div className="d-flex flex-wrap align-items-center gap-2">
            <span>{workOrder.workOrderStatus}</span>
            {workOrder.void !== "yes" ? transitionStatuses.map(status => (
                <button
                    key={status}
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    onClick={() => handleTransitionStatus(status as WorkOrder["workOrderStatus"])}
                >
                    {workOrder.workOrderStatus === "Hold" ? `Return to ${status}` : getTransitionButtonLabel(workOrder.workOrderStatus, status as WorkOrder["workOrderStatus"])}
                </button>
            )) : null}
        </div>
    );
    const curInfoData = workOrder.infoData;
    const curStatusData = workOrder.statusData;
    const techNameById = Object.fromEntries((workOrder.techs ?? []).map(tech => [tech.value, tech.label]));
    const fields: FieldDef[] = [
        { label: "Lab Number", value: workOrder.labNumber },
        { label: "Lab Number Date", value: workOrder.labNumberDate },
        { label: "Work Order Status", value: statusFieldValue },
        { label: "Assigned", value: assignmentText(workOrder.techs) },
        { label: "Organization Name", value: <Link children = {workOrder.organizationName} to = {`Organizations/${workOrder.organizationId}`}/> },
        { label: "Contact Name", value: <Link children = {workOrder.contactName} to = {`Contacts/${workOrder.contactId}`}/> },
        { label: "PO Number", value: workOrder.poNumber },
        { label: "Quote Number", value: workOrder.quoteNumber },
        { label: "Deadline", value: workOrder.deadline },
        { label: "Techs", value: workOrder.techs?.map(tech => <li key = {tech.value}><UserInfo id = {tech.value} name = {tech.label}/></li>)},
        { label: "Parts Received Date", value: workOrder.partsReceivedDate },
        {
            label: "Parts Received By",
            show: !!workOrder.receivedById,
            value: <UserInfo id = {workOrder.receivedById} name = {workOrder.receivedByName} />
        },
        { label: "Wor Order Date", value: workOrder.workOrderDate },
        {
            label: "Creator",
            show: !!workOrder.creatorId,
            value: (
                <>
                    <UserInfo
                        name = {workOrder.creatorName}
                        id = {workOrder.creatorId}
                    />
                    <span className="ms-2 text-muted">
                        {workOrder.createdAt}
                    </span>
                </>
            )
        },
        {
            label: "Updater",
            show: !!workOrder.updaterId,
            value: (
                <>
                    <UserInfo
                        name = {workOrder.updaterName}
                        id = {workOrder.updaterId}
                    />
                    <span className = "ms-2 text-muted">
                        {workOrder.updatedAt}
                    </span>
                </>
            )
        },
        {
            label: "Void Reason",
            show: !!workOrder.voidReason,
            value: <strong className = "text-danger">{workOrder.voidReason}</strong>
        },
        {
            label: "Validation Reason",
            show: !!workOrder.validateReason,
            value: <strong className = "text-success">{workOrder.validateReason}</strong>
        },
        {
            label: "Hold Reason",
            show: !!workOrder.holdReason,
            value: <strong className = "text-warning">{workOrder.holdReason}</strong>
        },
        {
            label: "Accepted Quantity",
            show: workOrder.acceptedQuantity !== undefined && workOrder.acceptedQuantity !== null && workOrder.acceptedQuantity !== "",
            value: workOrder.acceptedQuantity
        },
        {
            label: "Rejected Quantity",
            show: workOrder.rejectedQuantity !== undefined && workOrder.rejectedQuantity !== null && workOrder.rejectedQuantity !== "",
            value: workOrder.rejectedQuantity
        },
        {
            label: "Report Notes",
            show: !!workOrder.reportNotes,
            value: workOrder.reportNotes
        },
        {
            label: "Inspector Initials",
            show: !!workOrder.inspectorInitials,
            value: workOrder.inspectorInitials
        }
    ];
    return(
    <div>
        <div className = "table-responsive px-4">
            <table className = "table">
                <tbody className = "noTableTopBorder">
                    {fields
                    .filter(f => f.value && f.show !== false)
                    .map((f, idx) => (
                        <tr key={idx}>
                            <th>{f.label}</th>
                            <td>{f.value}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
        {workOrder.workOrderStatusHistory?.length ? (
        <div className = "table-responsive px-4 mt-4">
            <h4 className = "text-center"><strong>Work Order Status History</strong></h4>
            <table className = "table">
                <thead>
                    <tr>
                        <th>Status</th>
                        <th>Reason</th>
                        <th>Accepted Qty</th>
                        <th>Rejected Qty</th>
                        <th>Report Notes</th>
                        <th>Inspector Initials</th>
                        <th>User</th>
                        <th>DateTime</th>
                    </tr>
                </thead>
                <tbody>
                    {workOrder.workOrderStatusHistory.map(entry => (
                        <tr key={entry.id}>
                            <td>{entry.status}</td>
                            <td>{entry.holdReason ?? ""}</td>
                            <td>{entry.acceptedQuantity ?? ""}</td>
                            <td>{entry.rejectedQuantity ?? ""}</td>
                            <td>{entry.reportNotes ?? ""}</td>
                            <td>{entry.inspectorInitials ?? ""}</td>
                            <td><UserInfo id = {entry.userId} name = {entry.userName} /></td>
                            <td>{entry.createdAt}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
        ) : null}
        <div className = "p-3">
            <WorkOrderFileSystem workOrder = {workOrder}/>
        </div>
        {curInfoData.length?<>
        <h4 className = "text-center">
            <strong>TEST REQUIREMENT(S)</strong>
            <button
                children = "Download"
                type = "button"
                className = "btn btn-primary ms-3"
                onClick={handleDownloadInfoPDF}
            />
        </h4>
        <div className = "table-responsive px-4">
            <table className = "table">
                <thead>
                    <tr>
                        <th>TEST REQUIREMENT</th>
                        {curInfoData.map((v, i) => 
                            <th key = {i}>{v.label}</th>
                        )}
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <th>Part Number</th>
                        {curInfoData.map((v, i) => 
                            <td key = {i}>{v.pn}</td>
                        )}
                    </tr>
                    <tr>
                        <th>Quantity</th>
                        {curInfoData.map((v, i) => 
                            <td key = {i}>{v.qty}</td>
                        )}
                    </tr>
                    <tr>
                        <th>Process</th>
                        {curInfoData.map((v, i) => 
                            <td key = {i}>{v.proc}</td>
                        )}
                    </tr>
                    <tr>
                        <th>Criteria</th>
                        {curInfoData.map((v, i) => 
                            <td key = {i}>{v.criteria}</td>
                        )}
                    </tr>
                    <tr>
                        <th>BMTI Technician</th>
                        {curInfoData.map((v, i) => 
                            <td key = {i}>{v.tech}</td>
                        )}
                    </tr>
                </tbody>
            </table>
        </div></>:null}
        {services.map((a, i) => 
        curInfoData.filter(v => v.optgroup === a.label).length > 0?<Fragment key = {i}>
        <h4 className = "text-center"><strong>{a.label}</strong></h4>
        <div className = "table-responsive px-4">
            <table className = "table">
                <thead>
                    <tr>
                        <th>Step</th>
                        <th className="text-wrap">BMTI Process</th>
                        <th>Quantity</th>
                        <th>ETA</th>
                        <th>Duration</th>
                        <th>Notes</th>
                        <th className="text-wrap">Inspector Signature</th>
                        <th className="text-wrap">Optional Inspectors</th>
                    </tr>
                </thead>
                <tbody>
                    {processes(a, curInfoData).map((r, ri) => 
                    <tr key = {ri}>
                        <th>{r}</th>
                        <td>
                            {curStatusData[a.label][r]?.proc}
                        </td>
                        <td>
                            {curStatusData[a.label][r]?.qty} 
                        </td>
                        <td>
                            {curStatusData[a.label][r]?.eta}
                        </td>
                        <td>
                            {curStatusData[a.label][r]?.duration}
                        </td>
                        <td><span className = "cssNotes">{curStatusData[a.label][r]?.notes}</span></td>
                        <td className="text-wrap">{curStatusData[a.label][r]?.initial}</td>
                        <td className="text-wrap">
                            {curStatusData[a.label][r]?.optionalInspectors?.length
                                ? curStatusData[a.label][r].optionalInspectors
                                    ?.map(inspectorId => techNameById[inspectorId] ?? inspectorId)
                                    .join(", ")
                                : ""}
                        </td>
                    </tr>
                    )}
                </tbody>
            </table>
        </div>
        </Fragment>:null
        )}
        <div className = "px-3">
            <fieldset className = "rounded border px-4" style = {{minHeight:"100%", whiteSpace: "pre-wrap"}}>
                <legend className = "px-2 fw-bold" style = {{width:"auto"}}>Notes</legend>
                <div className = "cssNotes d-block mb-4">{workOrder.notes}</div>
            </fieldset>
        </div>
        <div className = "my-3">
            {workOrder.void === "yes"?
            <>
                {testerEmails.includes(user.email)?
                <button
                    children = "Validate"
                    type = "button"
                    className = "btn btn-primary"
                    onClick = {() => handleValidate(workOrder.id)}
                />:null}
                <button
                    children = "Delete"
                    type = "button"
                    className = "btn btn-danger ms-3"
                    onClick = {() => handleDelete(workOrder.id)}
                />
            </>:
            <>
                <button
                    children = "Download Scanning PDF"
                    type = "button"
                    className = "btn btn-info m-3"
                    onClick = {handleDownloadScanningPdf}
                />
                {workOrder.workOrderStatus === "Completed"?
                <button
                    children = "Download Complete PDF"
                    type = "button"
                    className = "btn btn-info m-3"
                    onClick = {handleDownloadStatusPdf}
                />
                :null}
                <Link
                    children = "Edit"
                    to = "Edit"
                    className = "btn btn-outline-primary ms-3"
                />
                <button
                    children = "Void"
                    type = "button"
                    className = "btn btn-danger ms-3"
                    onClick = {() => handleVoid(workOrder.id)}
                />
            </>}
            <Link
                children = "Back"
                to = ".."
                relative = "path"
                className = "btn btn-outline-secondary float-end"
            />
        </div>
    </div>
    )
}
